/**
 * Offline storage: an inventory cache and a write outbox.
 *
 * This is the part of the PWA that makes it usable at an actual convention.
 * The service worker can serve a cached *read*, but a sale is a *write*, and a
 * write that fails because the hall WiFi dropped cannot simply be lost.
 *
 * So:
 *
 * - **Inventory cache.** The last successful item list per database is kept in
 *   IndexedDB, so the app opens and can be browsed with no connection.
 * - **Outbox.** Every write that fails for network reasons is appended to a
 *   queue and replayed, in order, when connectivity returns. Sales must replay
 *   in the order they were taken or the stock maths comes out wrong, so the
 *   queue is strictly sequential and stops at the first failure rather than
 *   skipping ahead.
 *
 * IndexedDB is used rather than `localStorage` because a day's sales can
 * exceed localStorage's ~5MB ceiling, and because localStorage is synchronous
 * and would jank the UI mid-transaction.
 */

const DB_NAME = 'convention-pos';
const DB_VERSION = 1;
const STORE_CACHE = 'cache';
const STORE_OUTBOX = 'outbox';

/** A queued write waiting to reach the server. */
export interface OutboxEntry {
  id?: number;
  /** API path, e.g. `/items/123/sell`. */
  path: string;
  method: string;
  body: unknown;
  /** What this was, for showing the operator a readable pending list. */
  label: string;
  queuedAt: number;
  attempts: number;
  lastError: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

/** Open (and if needed create) the IndexedDB database. */
function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE);
      }
      if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
        db.createObjectStore(STORE_OUTBOX, { keyPath: 'id', autoIncrement: true });
      }
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error('IndexedDB unavailable'));
  });

  return dbPromise;
}

/** Run a transaction against one store and resolve with the request's result. */
async function withStore<T>(
  storeName: string,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest,
): Promise<T> {
  const db = await openDatabase();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const request = action(transaction.objectStore(storeName));
    request.onsuccess = () => resolve(request.result as T);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB error'));
  });
}

// --------------------------------------------------------------------------
// Inventory cache
// --------------------------------------------------------------------------

/** Store a value in the offline cache. Failures are non-fatal by design. */
export async function cacheSet(key: string, value: unknown): Promise<void> {
  try {
    await withStore(STORE_CACHE, 'readwrite', (store) => store.put(value, key));
  } catch {
    // Private browsing and full-disk conditions both make IndexedDB throw.
    // Losing the cache degrades the app; crashing it would end the sale.
  }
}

/** Read a value from the offline cache, or null when absent/unavailable. */
export async function cacheGet<T>(key: string): Promise<T | null> {
  try {
    const value = await withStore<T | undefined>(STORE_CACHE, 'readonly', (store) =>
      store.get(key),
    );
    return value ?? null;
  } catch {
    return null;
  }
}

// --------------------------------------------------------------------------
// Outbox
// --------------------------------------------------------------------------

/** Append a write to the outbox. */
export async function enqueue(
  entry: Omit<OutboxEntry, 'id' | 'queuedAt' | 'attempts' | 'lastError'>,
): Promise<void> {
  await withStore(STORE_OUTBOX, 'readwrite', (store) =>
    store.add({ ...entry, queuedAt: Date.now(), attempts: 0, lastError: '' }),
  );
}

/** Return every queued write, oldest first. */
export async function listOutbox(): Promise<OutboxEntry[]> {
  try {
    const entries = await withStore<OutboxEntry[]>(STORE_OUTBOX, 'readonly', (store) =>
      store.getAll(),
    );
    return entries.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  } catch {
    return [];
  }
}

/** How many writes are still waiting. */
export async function outboxCount(): Promise<number> {
  try {
    return await withStore<number>(STORE_OUTBOX, 'readonly', (store) => store.count());
  } catch {
    return 0;
  }
}

/** Remove one entry from the outbox. */
async function removeEntry(id: number): Promise<void> {
  await withStore(STORE_OUTBOX, 'readwrite', (store) => store.delete(id));
}

/** Update an entry after a failed attempt. */
async function updateEntry(entry: OutboxEntry): Promise<void> {
  await withStore(STORE_OUTBOX, 'readwrite', (store) => store.put(entry));
}

/** Discard every queued write. */
export async function clearOutbox(): Promise<void> {
  await withStore(STORE_OUTBOX, 'readwrite', (store) => store.clear());
}

/** Give up on an entry after this many failed attempts. */
const MAX_ATTEMPTS = 10;

/** Result of a flush pass. */
export interface FlushResult {
  sent: number;
  failed: number;
  dropped: number;
  remaining: number;
  errors: string[];
}

/**
 * Replay queued writes, in order.
 *
 * @param send A function that performs one request, throwing on failure.
 * @returns What happened, for reporting to the operator.
 *
 * Stops at the first network failure: a later sale must not be applied before
 * an earlier one, or stock counts drift. A write the *server* rejects (a 4xx,
 * e.g. the item was deleted) is not retried forever — it is dropped after
 * MAX_ATTEMPTS so one bad entry cannot block the rest of the day's sales.
 */
export async function flushOutbox(
  send: (entry: OutboxEntry) => Promise<void>,
): Promise<FlushResult> {
  const result: FlushResult = { sent: 0, failed: 0, dropped: 0, remaining: 0, errors: [] };
  const entries = await listOutbox();

  for (const entry of entries) {
    try {
      await send(entry);
      if (entry.id !== undefined) await removeEntry(entry.id);
      result.sent += 1;
    } catch (error) {
      const err = error as { status?: number; message?: string };
      const message = err.message ?? 'Unknown error';
      const isNetwork = err.status === 0 || err.status === 503;

      entry.attempts += 1;
      entry.lastError = message;

      if (!isNetwork && entry.attempts >= MAX_ATTEMPTS) {
        if (entry.id !== undefined) await removeEntry(entry.id);
        result.dropped += 1;
        result.errors.push(`Gave up on "${entry.label}": ${message}`);
        continue;
      }

      await updateEntry(entry);
      result.failed += 1;
      result.errors.push(`${entry.label}: ${message}`);

      if (isNetwork) {
        // Still offline. Leave the rest queued rather than reordering them.
        break;
      }
    }
  }

  result.remaining = await outboxCount();
  return result;
}
